// ============================================================================
// Credits & data sources (spec §13: "Record open-source attributions on an
// in-app credits page"). Attributes the open data/content the app actually
// uses, with licenses and the share-alike acknowledgements those licenses
// require. Add new sources here as features (e.g. stroke-order data) ship.
// ============================================================================

import { el } from '../dom.js';

function link(href, text) {
  return el('a', { href, target: '_blank', rel: 'noopener noreferrer', text });
}

function source({ title, used, license, links = [], note }) {
  return el('div.card.stack', { style: 'gap:var(--space-2)' }, [
    el('h2', { text: title }),
    el('div', {}, [el('strong', { text: 'Used for: ' }), el('span.muted', { text: used })]),
    el('div', {}, [el('strong', { text: 'License: ' }), el('span.muted', { text: license })]),
    note ? el('p.faint', { text: note }) : false,
    links.length
      ? el('div.row', { style: 'gap:var(--space-3)' }, links.map(([h, t]) => link(h, t)))
      : false,
  ]);
}

export function renderCredits({ navigate }) {
  return el('div.container.stack', {}, [
    el('div.card.stack', {}, [
      el('h1', { text: 'Credits & data sources' }),
      el('p.muted', {
        text:
          'The Korean Hanja app is built on open reference data and open standards. ' +
          'We gratefully acknowledge the sources below and honor their licenses, ' +
          'including attribution and share-alike terms where required.',
      }),
    ]),

    source({
      title: 'Unihan Database (Unicode Character Database)',
      used: 'English meanings (kDefinition), Korean readings (kHangul), radicals (kRSUnicode), and stroke counts (kTotalStrokes) for the character set.',
      license: 'Unicode License (permissive). © Unicode, Inc.',
      links: [
        ['https://www.unicode.org/charts/unihan.html', 'Unihan'],
        ['https://www.unicode.org/license.txt', 'Unicode License'],
      ],
    }),

    source({
      title: 'Korean Wiktionary — “한문 교육용 기초 한자 1800” appendix',
      used: 'The Ministry of Education “Basic 1,800 Hanja for Educational Use” list, the middle/high-school tier split, and each character’s 訓音 (hun + eum) reading.',
      license: 'Creative Commons Attribution-ShareAlike 3.0 (CC BY-SA 3.0).',
      note:
        'Per CC BY-SA, this derived content is attributed to Korean Wiktionary contributors and may be reused under the same license.',
      links: [
        ['https://ko.wiktionary.org/wiki/%EB%B6%80%EB%A1%9D:%ED%95%9C%EB%AC%B8_%EA%B5%90%EC%9C%A1%EC%9A%A9_%EA%B8%B0%EC%B4%88_%ED%95%9C%EC%9E%90_1800', 'Wiktionary appendix'],
        ['https://creativecommons.org/licenses/by-sa/3.0/', 'CC BY-SA 3.0'],
      ],
    }),

    source({
      title: 'Ministry of Education — Basic Hanja for Educational Use (한문 교육용 기초 한자)',
      used: 'The public standard defining which 1,800 characters (900 middle + 900 high school) form the curriculum backbone.',
      license: 'Public educational standard (Republic of Korea).',
    }),

    source({
      title: 'Revised Romanization of Korean',
      used: 'The romanization shown alongside readings and words (e.g. 학 → “hak”).',
      license: 'Official standard of the National Institute of Korean Language (NIKL).',
      links: [['https://www.korean.go.kr/', 'NIKL']],
    }),

    source({
      title: 'Google Cloud Text-to-Speech',
      used: 'On-demand pronunciation audio for characters, words, and sentences (cached, usage-capped).',
      license: 'Google Cloud service; audio generated under the project’s API terms.',
      links: [['https://cloud.google.com/text-to-speech', 'Google Cloud TTS']],
    }),

    source({
      title: 'Claude (Anthropic)',
      used: 'AI-assisted, approval-gated pedagogical content — example sentences, mnemonics, and etymology drafts. Reviewed before publication; app-owned.',
      license: 'Generated content owned by the app; produced with Anthropic’s Claude.',
      links: [['https://www.anthropic.com', 'Anthropic']],
    }),

    el('div.card.stack', {}, [
      el('h2', { text: 'Built with' }),
      el('p.muted', {
        text:
          'Supabase (Postgres, Auth, Edge Functions), Cloudflare, and Vite. ' +
          'The 두벌식 Hangul composition engine, SRS scheduler, and romanizer are original to this project.',
      }),
      el('div.row', {}, [
        el('button.btn', { text: '← How it works', onclick: () => navigate('#/learn') }),
        el('button.btn.btn--ghost', { text: 'Browse', onclick: () => navigate('#/browse') }),
      ]),
    ]),
  ]);
}
